/**
 * The global theme's structure is defined here, where the theme associates the list of components
 * with their respective theme types. The theme is passed via the Environment context
 * and utilized by components using the `useComponentTheme` hook.
 */

import type { ColorProps } from '../utils/UNSAFE_interpolations/colors';
import type { NestedKeyOf } from '../utils/UNSAFE_typeUtils';
import type { ColorScheme as TokenColorScheme } from '#Common/themes/generatedContract.tokens';
import type { Base } from '#Common/themes/manualContract.tokens';
/* components will directly import themes for now, we will reintroduce the component themes into the global theme after 15.0 release
import { LabelTheme } from '../UNSAFE_Label/themes/LabelStyles.css';
import { ReadonlyTextFieldInputTheme } from '../UNSAFE_TextField/themes/ReadonlyTextFieldInputStyles.css';
import { ReadonlyTextFieldTheme } from '../UNSAFE_TextField/themes/ReadonlyTextFieldStyles.css';
import { TextFieldInputTheme } from '../UNSAFE_TextField/themes/TextFieldInputStyles.css';
import { TextFieldTheme } from '../UNSAFE_TextField/themes/TextFieldStyles.css';
import { UserAssistanceTheme } from '../UNSAFE_UserAssistance/themes/UserAssistanceStyles.css';
import { MessageBannerTheme } from '../UNSAFE_MessageBanner/themes/MessageBannerStyles.css';
import { ComponentMessageTheme } from '../UNSAFE_ComponentMessage/themes/ComponentMessageStyles.css';
import { MessageToastTheme } from '../UNSAFE_MessageToast/themes/MessageToastStyles.css';
import { FormLayoutTheme } from '../UNSAFE_TextField/themes/FormLayoutStyles.css';
import { CollapsibleTheme } from '../UNSAFE_Collapsible/themes/CollapsibleStyles.css';
import { DialogTheme } from '../UNSAFE_Dialog/themes/DialogStyles.css';
import { DrawerPopupTheme } from '../UNSAFE_DrawerPopup/themes/DrawerPopupStyles.css';
import { DrawerLayoutTheme } from '../UNSAFE_LayoutPopup/themes/DrawerLayoutStyles.css';
import { WindowOverlayTheme } from '../UNSAFE_WindowOverlay/themes/WindowOverlayStyles.css';
import { TooltipContentTheme } from '../hooks/UNSAFE_useTooltip/themes/TooltipContentStyles.css';
import { SkeletonTheme } from '../UNSAFE_Skeleton/themes/SkeletonStyles.css';
import { AvatarTheme } from '../UNSAFE_Avatar/themes/AvatarStyles.css';
import { BaseButtonTheme } from '../UNSAFE_BaseButton/themes/BaseButtonStyles.css';
import { ButtonLayoutTheme } from '../UNSAFE_ButtonLayout/themes/ButtonLayoutStyles.css';
import { SplitMenuButtonTheme } from '../UNSAFE_SplitMenuButton/themes/SplitMenuButtonStyles.css';
import { RadioTheme } from '../UNSAFE_RadioItem/themes/RadioStyles.css';
import { RadioIconTheme } from '../UNSAFE_RadioItem/themes/RadioIconStyles.css';
import { TrainTheme } from '../UNSAFE_Train/themes/TrainStyles.css';
import { IndexerTheme } from '../UNSAFE_Indexer/themes/IndexerStyles.css';
import { LinkTheme } from '../UNSAFE_Link/themes/LinkStyles.css';
import { GroupedListTheme } from '../UNSAFE_GroupedList/themes/GroupedListStyles.css';
import { TabBarItemTheme } from '../UNSAFE_TabBarItem/themes/TabBarItemStyles.css';
import { PopupTheme } from '../UNSAFE_Popup/themes/PopupStyles.css';
import { ProgressBarTheme } from '../UNSAFE_ProgressBar/themes/ProgressBarStyles.css';
import { ProgressCircleTheme } from '../UNSAFE_ProgressCircle/themes/ProgressCircleStyles.css';
import { FilePickerTheme } from '../UNSAFE_FilePicker/themes/FilePickerStyles.css';
import { MenuSeparatorTheme } from '../UNSAFE_Menu/themes/MenuSeparatorStyles.css';
import { MenuItemTheme } from '../UNSAFE_Menu/themes/MenuItemStyles.css';
import { NavigationListItemTheme } from '../UNSAFE_NavigationList/themes/NavigationListItemStyles.css';
import { BadgeTheme } from '../UNSAFE_Badge/themes/BadgeStyles.css';
import { RatingGaugeTheme } from '../UNSAFE_RatingGauge/themes/RatingGaugeStyles.css';
import { FloatingTheme } from '../UNSAFE_Floating/themes/FloatingStyles.css';
import { MeterBarTheme } from '../UNSAFE_MeterBar/themes/MeterBarStyles.css';
import { MeterCircleTheme } from '../UNSAFE_MeterCircle/themes/MeterCircleStyles.css';
*/
// types associated with environment variables related to theming
type ColorScheme = (typeof colorThemes)[number];
type Scale = 'sm' | 'md' | 'lg';
type Obj = Record<string, any>;
type RuntimeFn<V> = (options?: V) => string;

// commonly used props related to theming
type ThemeModifiers = {
  colorScheme: ColorScheme;
  scale: Scale;
  currentBgColor: ColorProps['backgroundColor'];
};

type ComponentThemeType<Variants = Obj, Styles = Obj> = {
  multiVariantStyles: RuntimeFn<any>;
  variants?: Variants;
  styles?: Styles;
  baseTheme?: string;
};

type VariantOptions<V extends object> = Partial<NestedKeyOf<V>>;

type Theme = {
  name: string;
  base: Base;
  colorScheme: TokenColorScheme;
  scale: Obj;
};

/* components will directly import themes for now, we will reintroduce the component themes into the global theme after 15.0 release
type Theme = {
  name: string;
  components: {
    Avatar: AvatarTheme;
    Badge: BadgeTheme;
    BaseButton: BaseButtonTheme;
    ButtonLayout: ButtonLayoutTheme;
    Label: LabelTheme;
    Link: LinkTheme;
    ComponentMessage: ComponentMessageTheme;
    MessageBanner: MessageBannerTheme;
    MessageToast: MessageToastTheme;
    FilePicker: FilePickerTheme;
    FormLayout: FormLayoutTheme;
    Popup: PopupTheme;
    ProgressBar: ProgressBarTheme;
    ProgressCircle: ProgressCircleTheme;
    TextField: TextFieldTheme;
    TextFieldInput: TextFieldInputTheme;
    RadioItem: RadioTheme;
    RadioIcon: RadioIconTheme;
    RatingGauge: RatingGaugeTheme;
    ReadonlyTextField: ReadonlyTextFieldTheme;
    ReadonlyTextFieldInput: ReadonlyTextFieldInputTheme;
    Skeleton: SkeletonTheme;
    SplitMenuButton: SplitMenuButtonTheme;
    UserAssistance: UserAssistanceTheme;
    Collapsible: CollapsibleTheme;
    Dialog: DialogTheme;
    DrawerPopup: DrawerPopupTheme;
    DrawerLayout: DrawerLayoutTheme;
    WindowOverlay: WindowOverlayTheme;
    TabBarItem: TabBarItemTheme;
    TooltipContent: TooltipContentTheme;
    Train: TrainTheme;
    Indexer: IndexerTheme;
    GroupedList: GroupedListTheme;
    MenuSeparator: MenuSeparatorTheme;
    MenuItem: MenuItemTheme;
    MeterBar: MeterBarTheme;
    MeterCircle: MeterCircleTheme;
    NavigationListItem: NavigationListItemTheme;
    Floating: FloatingTheme;
    // TODO: Components to be converted to the theming system
    // ButtonTheme,
    // ChipTheme,
    // CollectionTheme,
    // ComponentMessageTheme,
    // HeadingTheme,
    // HighlightTextTheme,
    // IconTheme,
    // IconsTheme,
    // InputPasswordTheme,
    // InputTextTheme,
    // LabelValueLayoutTheme,
    // LayerTheme,
    // PrefixSuffixTheme,
    // SelectMultipleTheme,
    // SkeletonTheme,
    // TextAreaTheme,
    // TextAreaAutosizeTheme,
    // ThemedIconsTheme,
    // ViewTheme,
    // VirtualizedCollectionTheme
  };
  base: Obj;
  colorScheme: Obj;
  scale: Obj;
  defaultProps: {
    colorScheme?: ColorScheme;
    scale?: Scale;
  };
};
*/

type CompoundVariantStyles<T> = {
  variants: Partial<T>;
  style: string | object;
}[];

const colorThemes = ['light', 'dark'] as const;

// Constants for colorscheme classnames
// These are needed to support applications using legacy code
const DARK_CLASS = 'oj-c-colorscheme-dark';
const LIGHT_CLASS = 'oj-c-colorscheme-light';
const INVERT_CLASS = 'oj-color-invert'; // legacy class name for compatibility
const COLORSCHEME_DEPENDENT_CLASS = 'oj-c-colorscheme-dependent'; // The dependent class name is used to force re-evaluation
// of CSS variables that reference colorScheme token vars
// Constants for scale classnames
const SCALE_SM_CLASS = 'oj-scale-sm'; // legacy class name for compatibility
// We need to specify :root.oj-scale-sm in the selector due to how legacy switches scale
const SCALE_SM_SELECTORS = `.${SCALE_SM_CLASS}, :root.${SCALE_SM_CLASS}`;
const SCALE_MD_CLASS = 'oj-scale-md'; // legacy class name for compatibility
// We need to specify :root.oj-scale-md in the selector due to how legacy switches scale
const SCALE_MD_SELECTORS = `.${SCALE_MD_CLASS}, :root.${SCALE_MD_CLASS}`;
const SCALE_LG_CLASS = 'oj-scale-lg'; // legacy class name for compatibility
const SCALE_LG_SELECTORS = `.${SCALE_LG_CLASS}`; // 'lg' scale only has a single selector.
const SCALE_DEFAULT_SELECTORS = ':root';
const SCALE_DEPENDENT_CLASS = 'oj-scale-dependent'; // The dependent class name is used to force re-evaluation of CSS variables
// For legacy compatibility, we need to add :root.${SCALE_SM_CLASS} and :root${SACEL_MD_CLASS} so that the
// :root vars that refer to scale vars will re-evaluate in legacy
const SCALE_DEPENDENT_SELECTORS = `.${SCALE_DEPENDENT_CLASS}, :root.${SCALE_SM_CLASS}, :root.${SCALE_MD_CLASS}`;

// that reference scale token vars

export type {
  Theme,
  ComponentThemeType,
  ColorScheme,
  Scale,
  ThemeModifiers,
  CompoundVariantStyles,
  VariantOptions
};

export {
  colorThemes,
  DARK_CLASS,
  LIGHT_CLASS,
  INVERT_CLASS,
  COLORSCHEME_DEPENDENT_CLASS,
  SCALE_SM_CLASS,
  SCALE_SM_SELECTORS,
  SCALE_MD_CLASS,
  SCALE_MD_SELECTORS,
  SCALE_LG_CLASS,
  SCALE_LG_SELECTORS,
  SCALE_DEPENDENT_CLASS,
  SCALE_DEPENDENT_SELECTORS,
  SCALE_DEFAULT_SELECTORS
};
